import {
  Home,
  Briefcase,
  ClipboardList,
  Building2,
  PlusCircle,
  Search,
  Bell,
  Megaphone,
} from "lucide-react";
import { KpiCard } from "../components/KpiCard.jsx";
import PremiumClubCard from "../components/PremiumClubCard.jsx";
import HomeOfferBanner from "../components/offers/HomeOfferBanner.jsx";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Cell,
} from "recharts";
import { useEffect, useState } from "react";
import apiClient from "../common/apiClient";
import Cookies from "js-cookie";
import decryptResponse from "../common/decrypt";
import { linkifyText } from "../utils/linkifyText.jsx";
import { useUserProfile } from "../contexts/UserProfileContext";
import { FEATURES } from "../constants/feature.constants";
import { showSessionExpiredPopup } from "../components/SessionExpiredPopup";

const propertyActivity = [
  { day: "Mon", count: 45 },
  { day: "Tue", count: 62 },
  { day: "Wed", count: 38 },
  { day: "Thu", count: 71 },
  { day: "Fri", count: 55 },
  { day: "Sat", count: 29 },
  { day: "Sun", count: 18 },
];

const requirementActivity = [
  { day: "Mon", count: 22 },
  { day: "Tue", count: 35 },
  { day: "Wed", count: 28 },
  { day: "Thu", count: 41 },
  { day: "Fri", count: 38 },
  { day: "Sat", count: 15 },
  { day: "Sun", count: 10 },
];

const propertyFlowZones = [
  { zone: "West Zone", inflow: 120, outflow: 85 },
  { zone: "East Zone", inflow: 95, outflow: 72 },
  { zone: "South Zone", inflow: 78, outflow: 60 },
  { zone: "North Zone", inflow: 64, outflow: 48 },
  { zone: "Central", inflow: 142, outflow: 110 },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { userProfile, hasFeature } = useUserProfile();

  const notifications = [];

  const [propertyCounts, setPropertyCounts] = useState({});
  const [allCounts, setAllCounts] = useState(null);
  const [builderCount, setBuilderCount] = useState(0);
  const [notices, setNotices] = useState([]);
  const [zoneStats, setZoneStats] = useState([]);
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [loadingAllCounts, setLoadingAllCounts] = useState(true);

  const checkSession = async () => {
    try {
      const token = Cookies.get("token");
      if (!token) return;

      const sessionResponse = await apiClient(
        "get",
        "/cjidnvij/ceksfbuebijn/user/v2/checkSession",
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      let finalData = sessionResponse;
      if (sessionResponse?.error?.encrypted) {
        const decrypted = decryptResponse(sessionResponse.error.encrypted);
        if (decrypted) finalData = decrypted;
      }

      if (
        finalData.success === false ||
        finalData.status === "error" ||
        finalData.status === 401
      ) {
        if (finalData.action === "logout" || finalData.status === 401) {
          handleLogout(finalData.message || "Session expired");
        } else if (finalData.error?.action === "logout") {
          handleLogout(finalData.error.message || "Session expired");
        }
      }
    } catch (error) {
      console.error("Error checking session:", error);
    }
  };

  const handleLogout = (message) => {
    Cookies.remove("token");
    Cookies.remove("userId");
    Cookies.remove("isPremium");
    Cookies.remove("deviceId");
    localStorage.clear();
    navigate("/login");
    showSessionExpiredPopup(message);
  };

  // Dashboard counts are city-agnostic: no `city` filter is sent, so the KPI
  // cards always show the totals across every city.
  const fetchCounts = async () => {
    setLoadingCounts(true);
    try {
      const userId = Cookies.get("userId");
      const res = await apiClient(
        "get",
        "/cjidnvij/ceksfbuebijn/user/counts/fjkbfhwb/fkjbwdiwhbdjwkfjwbj",
        { userId: userId || "", status: "Probroker" },
      );
      if (res.success) {
        setPropertyCounts(res.data);
      }
    } catch (error) {
      console.error("Error fetching property counts:", error);
    } finally {
      setLoadingCounts(false);
    }
  };

  const fetchAllCounts = async () => {
    setLoadingAllCounts(true);
    try {
      const userId = Cookies.get("userId");
      const res = await apiClient(
        "get",
        "/cjidnvij/ceksfbuebijn/user/allCount",
        { userId: userId || "" },
      );
      if (res.success) {
        setAllCounts(res.data?.data || res.data || null);
      }
    } catch (error) {
      console.error("Error fetching all counts:", error);
    } finally {
      setLoadingAllCounts(false);
    }
  };

  useEffect(() => {
    const fetchBuilderProjects = async () => {
      try {
        const token = Cookies.get("token");
        const userId = Cookies.get("userId");

        const payload = {
          brokerId: userId || "",
          listedBy: "builder",
          type: "",
          propertyCurrentStatus: "Available",
          verifyStatus: "verified",
          page: 0,
          size: 1, // We only need the total count
        };

        const res = await apiClient(
          "post",
          "/cjidnvij/ceksfbuebijn/user/brokerproperties/filters",
          payload,
          {
            headers: token
              ? {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                }
              : { "Content-Type": "application/json" },
          },
        );

        if (res.success) {
          // Based on common pagination patterns, total might be in res.data.totalElements or res.data.total
          const total =
            res.data?.totalElements ||
            res.data?.total ||
            (Array.isArray(res.data?.data) ? res.data.data.length : 0);
          setBuilderCount(total);
        }
      } catch (error) {
        console.error("Error fetching builder projects count:", error);
      }
    };

    const fetchNotices = async () => {
      try {
        const token = Cookies.get("token");
        const res = await apiClient(
          "get",
          "/ijscui/probroker-admin/ceknvuhbd/cjwbhusb/protected/v2/system-notice/all",
          {},
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );

        if (res.success) {
          const now = new Date();
          const items = (res.data?.data || []).filter((n) => {
            const isActive = n.active === true;
            const notExpired = n.expiresAt ? new Date(n.expiresAt) > now : true;
            return isActive && notExpired;
          });
          setNotices(items);
        }
      } catch (e) {
        console.error("Error fetching notices:", e);
      }
    };

    const fetchZoneStats = async () => {
      try {
        const res = await apiClient("get", "/api/zone-stats");
        if (res.success) {
          setZoneStats(res.data?.data || res.data || []);
        }
      } catch (error) {
        console.error("Error fetching zone stats:", error);
      }
    };

    checkSession();
    fetchCounts();
    fetchAllCounts();
    fetchBuilderProjects();
    fetchNotices();
    fetchZoneStats();
  }, []);

  const mappedPropertyActivity =
    propertyCounts?.propertyFlow?.length > 0
      ? propertyCounts.propertyFlow.map((item) => ({
          day: item.day,
          count: item.allCount || 0,
        }))
      : propertyActivity;

  const mappedRequirementActivity =
    propertyCounts?.propertyFlow?.length > 0
      ? propertyCounts.propertyFlow.map((item) => ({
          day: item.day,
          count: item.rcount || 0,
        }))
      : requirementActivity;

  return (
    <div className="space-y-6">
      {/* Premium Club interest teaser -- above everything else, all devices */}
      {/* <PremiumClubCard /> */}

      {/* Live campaign offer (renders nothing when none is live) */}
      <HomeOfferBanner />

      {/* Title + Search Button */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold">{t("Dashboard", "ડેશબોર્ડ")}</h1>
        <div className="flex items-center gap-3 flex-wrap ml-auto justify-end min-w-0 max-w-full">
          <Button
            size="sm"
            className="gradient-primary border-0 text-primary-foreground gap-1.5 shadow-md"
            onClick={() => navigate("/filter")}
          >
            <Search size={16} />
            {t("Search Property", "પ્રોપર્ટી શોધો")}
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-3 lg:gap-4">
        <KpiCard
          title={t("Owner Properties", "માલિક પ્રોપર્ટી")}
          value={propertyCounts.totalActiveProperties?.toLocaleString() || "0"}
          icon={Home}
          trend={t()}
          to="/owner-properties"
          iconBg="bg-[hsl(262,83%,95%)]"
          iconColor="text-[hsl(262,60%,45%)]"
          disabled={!hasFeature(FEATURES.OWNER_PROPERTY)}
          loading={loadingCounts}
        />
        <KpiCard
          title={t("Broker Properties", "બ્રોકર પ્રોપર્ટી")}
          value={allCounts?.brokerProperties?.total?.toLocaleString() || "0"}
          icon={Briefcase}
          trend={t()}
          to="/broker-properties"
          iconBg="bg-[hsl(30,100%,94%)]"
          iconColor="text-[hsl(30,100%,45%)]"
          disabled={!hasFeature(FEATURES.BROKER_PROPERTY)}
          loading={loadingAllCounts}
        />
        <KpiCard
          title={t("Broker Requirements", "બ્રોકર જરૂરિયાત")}
          value={allCounts?.brokerRequirements?.total?.toLocaleString() || "0"}
          icon={ClipboardList}
          trend={t()}
          to="/broker-properties"
          iconBg="bg-[hsl(170,60%,92%)]"
          iconColor="text-[hsl(170,60%,35%)]"
          disabled={!hasFeature(FEATURES.AGENT_REQUIREMENT) && !hasFeature(FEATURES.BROKER_PROPERTY)}
          loading={loadingAllCounts}
        />
        {/* <KpiCard
          title={t("Builder Projects", "બિલ્ડર પ્રોજેક્ટ્સ")}
          value={allCounts?.builderProperties?.total?.toLocaleString() || "0"}
          icon={Building2}
          trend={t()}
          to="/residential-rental/builder"
          iconBg="bg-[hsl(210,80%,94%)]"
          iconColor="text-[hsl(210,80%,45%)]"
          disabled={!hasFeature(FEATURES.BUILDER_PROPERTY)}
          loading={loadingAllCounts}
        /> */}
      </div>

      {/* Notifications - Only show if there are active notices */}
      {notices.length > 0 && (
        <div className="glass-card rounded-xl p-3 lg:p-4 space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <Bell size={16} className="lg:w-[18px] lg:h-[18px] text-primary" />
            <span className="font-semibold text-xs lg:text-sm">
              {t("Notifications", "સૂચનાઓ")}
            </span>
            <span className="ml-auto text-xs font-bold gradient-primary text-primary-foreground px-2 py-0.5 lg:px-2.5 rounded-full">
              {notices.length} {t("new", "નવી")}
            </span>
          </div>

          {/* Dynamic Notices from API */}
          {notices.map((notice, index) => (
            <div
              key={`notice-${notice._id || index}`}
              className="bg-[hsl(262,83%,95%)] border border-[hsl(262,83%,90%)] rounded-lg px-3 lg:px-4 py-2 lg:py-3 text-xs lg:text-sm flex items-start gap-2 lg:gap-3"
            >
              <Megaphone
                size={14}
                className="lg:w-4 lg:h-4 text-[hsl(262,60%,45%)] mt-0.5 flex-shrink-0"
              />
              <div className="flex-1">
                <div className="text-foreground break-words">
                  {linkifyText(
                    notice.message,
                    "text-primary font-semibold underline whitespace-nowrap hover:opacity-80",
                    t("Click Here", "અહીં ક્લિક કરો")
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4">
        <div className="glass-card rounded-xl p-3 lg:p-5">
          <h3 className="font-semibold mb-3 lg:mb-4 text-sm lg:text-base">
            {t(
              "Property Activity (Last 7 Days)",
              "પ્રોપર્ટી પ્રવૃત્તિ (છેલ્લા ૭ દિવસ)",
            )}
          </h3>
          <ResponsiveContainer width="100%" height={200} className="lg:!h-[220px]">
            <BarChart data={mappedPropertyActivity}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="hsl(var(--border))"
              />
              <XAxis dataKey="day" fontSize={12} />
              <YAxis fontSize={12} />
              <Tooltip />
              <Bar
                dataKey="count"
                fill="hsl(262, 83%, 58%)"
                radius={[6, 6, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
         <div className="glass-card rounded-xl p-3 lg:p-5">
          <h3 className="font-semibold mb-3 lg:mb-4 text-sm lg:text-base">
            {t("Quick Actions", "ઝડપી ક્રિયાઓ")}
          </h3>
          <div className="grid grid-cols-3 gap-2 lg:gap-3">
            {[
              {
                label: t("Add Property", "પ્રોપર્ટી ઉમેરો"),
                icon: PlusCircle,
                path: "/property-listed",
                bg: "bg-[hsl(262,83%,92%)]",
                color: "text-primary",
              },
              {
                label: t("Search Property", "પ્રોપર્ટી શોધો"),
                icon: Search,
                path: "/filter",
                bg: "bg-[hsl(170,60%,88%)]",
                color: "text-[hsl(170,60%,35%)]",
              },
              {
                label: t("Match Requirement", "જરૂરિયાત મેચ"),
                icon: ClipboardList,
                path: "/match-property",
                bg: "bg-[hsl(30,100%,90%)]",
                color: "text-[hsl(30,100%,45%)]",
              },
            ].map((action) => (
              <button
                key={action.label}
                onClick={() => navigate(action.path)}
                className="flex flex-col items-center gap-2 lg:gap-3 p-3 lg:p-6 rounded-xl border border-border hover:border-primary hover:bg-primary-light transition-all duration-200 group"
              >
                <div
                  className={`w-10 h-10 lg:w-14 lg:h-14 rounded-full ${action.bg} flex items-center justify-center`}
                >
                  <action.icon
                    size={22}
                    className={`lg:w-7 lg:h-7 ${action.color} group-hover:scale-110 transition-transform`}
                  />
                </div>
                <span className="text-xs lg:text-sm font-medium text-muted-foreground group-hover:text-primary transition-colors text-center leading-tight">
                  {action.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      
      </div>

      {/* Property Flow Zone Wise & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4">
        <div className="glass-card rounded-xl p-3 lg:p-5">
          <h3 className="font-semibold mb-3 lg:mb-4 text-sm lg:text-base">
            {t("Property Flow — Zone Wise", "પ્રોપર્ટી ફ્લો — ઝોન પ્રમાણે")}
          </h3>
          <ResponsiveContainer width="100%" height={220} className="lg:!h-[250px]">
            <BarChart
              data={zoneStats.length > 0 ? zoneStats : propertyFlowZones}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="hsl(var(--border))"
              />
              <XAxis dataKey="zone" fontSize={11} />
              <YAxis fontSize={12} />
              <Tooltip />
              {zoneStats.length > 0 ? (
                <Bar
                  dataKey="properties"
                  name={t("Properties", "પ્રોપર્ટી")}
                  radius={[4, 4, 0, 0]}
                >
                  {zoneStats.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              ) : (
                <>
                  <Bar
                    dataKey="inflow"
                    name={t("Inflow", "ઇનફ્લો")}
                    fill="hsl(262, 83%, 58%)"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="outflow"
                    name={t("Outflow", "આઉટફ્લો")}
                    fill="hsl(270, 75%, 68%)"
                    radius={[4, 4, 0, 0]}
                  />
                </>
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
  <div className="glass-card rounded-xl p-3 lg:p-5">
          <h3 className="font-semibold mb-3 lg:mb-4 text-sm lg:text-base">
            {t(
              "Requirements Activity (Last 7 Days)",
              "જરૂરિયાત પ્રવૃત્તિ (છેલ્લા ૭ દિવસ)",
            )}
          </h3>
          <ResponsiveContainer width="100%" height={200} className="lg:!h-[220px]">
            <LineChart data={mappedRequirementActivity}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="hsl(var(--border))"
              />
              <XAxis dataKey="day" fontSize={12} />
              <YAxis fontSize={12} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="count"
                stroke="hsl(262, 83%, 58%)"
                strokeWidth={2}
                dot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>       
      </div>
    </div>
  );
};

export default Dashboard;
